import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import { formattedDate, formattedDateTypeTwo, getCurrentDateTime1 } from "../../../../Common/helper/helpar";

export const FrequencyBinsInnerExport = ({ data, fromDate, toDate }) => {
  const currentDateTime = getCurrentDateTime1();
  const reportTitle = `Detailed Shift Wise Data Report as on ` + `(${fromDate})`;
  const topHeaderText = `Detailed Shift Wise Data Report is generated on (${currentDateTime})`;

  /* ================= SUM FUNCTION ================= */

  const sum = (key) =>
    data.reduce((acc, item) => acc + (parseFloat(item[key]) || 0), 0);

  /* ===================== EXCEL DOWNLOAD ===================== */

  const downloadAsExcel = async () => {
    if (!data || data.length === 0) {
      alert("No data available to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Detailed Shift Wise Data Report");

    /* ===== Row 1 - Generated On ===== */
    worksheet.addRow([topHeaderText]);
    worksheet.mergeCells(1, 1, 1, 14);
    worksheet.getCell(1, 1).alignment = { horizontal: "center" };
    worksheet.getCell(1, 1).font = { bold: true };

    /* ===== Row 2 - Title ===== */
    worksheet.addRow([reportTitle]);
    worksheet.mergeCells(2, 1, 2, 14);
    worksheet.getCell(2, 1).alignment = { horizontal: "center", wrapText: true };
    worksheet.getCell(2, 1).font = { bold: true, size: 14 };

    worksheet.addRow([]);

    /* ===== Header Row ===== */
    const headerRow = worksheet.addRow([
      "S.No",
      "Shift",
      "GVPs - Points from Masters",
      "GVPs - Points Cleared",
      "GVPs - Cleared %",
      "GVPs - Not Cleared",
      "GVPs - Not Cleared %",
      "Sweeping Points - Points from Masters",
      "Sweeping Points - Points Cleared",
      "Sweeping Points - Cleared %",
      "Sweeping Points - Not Cleared",
      "Sweeping Points - Not Cleared %",
      "Others - Cleared",
      "Others - Not Cleared",
    ]);

    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center", wrapText: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F6B1B1" }, // Light Blue
      };
    });

    /* ===== Data Rows ===== */
    data.forEach((item, index) => {
      worksheet.addRow([
        index + 1,
        item.shiftName || "",
        item.gvpMasters || 0,
        item.gvpCleared || 0,
        item.gvpMasters ? ((item.gvpCleared / item.gvpMasters) * 100).toFixed(2) + "%" : "0%",
        item.gvpNotCleared || 0,
        item.gvpMasters ? ((item.gvpNotCleared / item.gvpMasters) * 100).toFixed(2) + "%" : "0%",
        item.sweepMasters || 0,
        item.sweepCleared || 0,
        item.sweepMasters ? ((item.sweepCleared / item.sweepMasters) * 100).toFixed(2) + "%" : "0%",
        item.sweepNotCleared || 0,
        item.sweepMasters ? ((item.sweepNotCleared / item.sweepMasters) * 100).toFixed(2) + "%" : "0%",
        item.othersCleared || 0,
        item.othersNotCleared || 0,
      ]).eachCell((cell) => {
        cell.alignment = { horizontal: "center" };
      });
    });

    /* ===== Total Row ===== */
    const totalGvpMasters = sum("gvpMasters");
    const totalGvpCleared = sum("gvpCleared");
    const totalSweepMasters = sum("sweepMasters");
    const totalSweepCleared = sum("sweepCleared");

    worksheet.addRow([
      "",
      "Total",
      totalGvpMasters,
      totalGvpCleared,
      totalGvpMasters ? ((totalGvpCleared / totalGvpMasters) * 100).toFixed(2) + "%" : "0%",
      sum("gvpNotCleared"),
      totalGvpMasters ? ((sum("gvpNotCleared") / totalGvpMasters) * 100).toFixed(2) + "%" : "0%",
      totalSweepMasters,
      totalSweepCleared,
      totalSweepMasters ? ((totalSweepCleared / totalSweepMasters) * 100).toFixed(2) + "%" : "0%",
      sum("sweepNotCleared"),
      totalSweepMasters ? ((sum("sweepNotCleared") / totalSweepMasters) * 100).toFixed(2) + "%" : "0%",
      sum("othersCleared"),
      sum("othersNotCleared"),
    ]).eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center" };
    });

    worksheet.columns = new Array(14).fill({ width: 20 });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      "DetailedShiftWiseDataReport.xlsx");
  };

  /* ===================== PDF DOWNLOAD ===================== */

  const downloadAsPdf = () => {
    if (!data || data.length === 0) {
      alert("No data available to export");
      return;
    }

    const doc = new jsPDF("l", "mm", "a4");
    const styledText = (text, x, y, size, options = {}) => {
        const originalSize = doc.getFontSize();
        doc.setFontSize(size);
        doc.text(text, x, y, options);
        doc.setFontSize(originalSize); // Reverts back automatically
    };

    /* ===== Generated On (Right) ===== */
    // doc.setFontSize(10);
    // doc.text(`Report Generated On: ${reportGeneratedOn}`, 280, 10, { align: "right" });

    /* ===== Title ===== */
    doc.setFontSize(14);
    const pageWidth = doc.internal.pageSize.width;
    const titleX = pageWidth / 2;
    styledText(topHeaderText, titleX, 15, 12, { align: "center" });
    styledText(reportTitle, titleX, 24, 14, { align: "center" });

    const tableColumn = [
      "S.No",
      "Shift",
      "GVP Masters",
      "GVP Cleared",
      "Cleared %",
      "GVP Not Cleared",
      "Not Cleared %",
      "Sweep Masters",
      "Sweep Cleared",
      "Cleared %",
      "Sweep Not Cleared",
      "Not Cleared %",
      "Others Cleared",
      "Others Not Cleared",
    ];

    const tableRows = data.map((item, index) => [
      index + 1,
      item.shiftName || "",
      item.gvpMasters || 0,
      item.gvpCleared || 0,
      item.gvpMasters ? ((item.gvpCleared / item.gvpMasters) * 100).toFixed(2) + "%" : "0%",
      item.gvpNotCleared || 0,
      item.gvpMasters ? ((item.gvpNotCleared / item.gvpMasters) * 100).toFixed(2) + "%" : "0%",
      item.sweepMasters || 0,
      item.sweepCleared || 0,
      item.sweepMasters ? ((item.sweepCleared / item.sweepMasters) * 100).toFixed(2) + "%" : "0%",
      item.sweepNotCleared || 0,
      item.sweepMasters ? ((item.sweepNotCleared / item.sweepMasters) * 100).toFixed(2) + "%" : "0%",
      item.othersCleared || 0,
      item.othersNotCleared || 0,
    ]);

    /* ===== Total Row ===== */
    const totalGvpMasters = sum("gvpMasters");
    const totalGvpCleared = sum("gvpCleared");
    const totalSweepMasters = sum("sweepMasters");
    const totalSweepCleared = sum("sweepCleared");

    tableRows.push([
      "",
      "Total",
      totalGvpMasters,
      totalGvpCleared,
      totalGvpMasters ? ((totalGvpCleared / totalGvpMasters) * 100).toFixed(2) + "%" : "0%",
      sum("gvpNotCleared"),
      totalGvpMasters ? ((sum("gvpNotCleared") / totalGvpMasters) * 100).toFixed(2) + "%" : "0%",
      totalSweepMasters,
      totalSweepCleared,
      totalSweepMasters ? ((totalSweepCleared / totalSweepMasters) * 100).toFixed(2) + "%" : "0%",
      sum("sweepNotCleared"),
      totalSweepMasters ? ((sum("sweepNotCleared") / totalSweepMasters) * 100).toFixed(2) + "%" : "0%",
      sum("othersCleared"),
      sum("othersNotCleared"),
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 28,
      styles: { fontSize: 7 },
      theme: "grid",
      headStyles: {
        fillColor: [0, 102, 204], // Medium Blue
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      didParseCell: (data) => {
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save("DetailedShiftWiseDataReport.pdf");
  };

  return (
    <div className="d-flex justify-content-end mt-3 gap-2">
      
      <button className="btn btn-excel" onClick={downloadAsPdf}>
        <i className="fa-solid fa-file-pdf me-1"></i> PDF
      </button>
      <button className="btn btn-excel" onClick={downloadAsExcel}>
        <i className="fa-solid fa-file-excel me-1"></i> Excel
      </button>
    </div>
  );
};
